import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
} from "recharts";
import {
  Plus, Send, Trophy, Trash2, Loader2, FlaskConical, Eye, MousePointerClick,
  ChevronDown, ChevronUp, Play, Crown, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  draft: "bg-gray-500/10 text-gray-500 border-gray-500/20",
  sending: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  completed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  failed: "bg-red-500/10 text-red-600 border-red-500/20",
};

export function ABTestManager({ darkMode: dm }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [results, setResults] = useState({});
  const [form, setForm] = useState({ title: "", subject_a: "", subject_b: "", body_html: "" });
  const [sending, setSending] = useState({});

  const headers = { Authorization: `Bearer ${localStorage.getItem("gmp_token")}` };

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ab-tests`, { headers });
      setTests(res.data);
    } catch { toast.error("Failed to load A/B tests"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const handleCreate = async () => {
    if (!form.subject_a.trim() || !form.subject_b.trim()) {
      toast.error("Both subject lines are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/ab-tests`, {
        title: form.title || "Untitled Test",
        subject_a: form.subject_a,
        subject_b: form.subject_b,
        body_html: form.body_html || "<p>Hello {{name}},</p><p>Check out our latest updates!</p>",
        audience_type: "all",
        audience_filter: {},
      }, { headers });
      toast.success("A/B test created");
      setCreating(false);
      setForm({ title: "", subject_a: "", subject_b: "", body_html: "" });
      fetchTests();
    } catch { toast.error("Failed to create test"); }
  };

  const handleSend = async (id) => {
    setSending(p => ({ ...p, [id]: true }));
    try {
      await axios.post(`${API}/admin/ab-tests/${id}/send`, {}, { headers });
      toast.success("A/B test sending started");
      setTimeout(fetchTests, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(p => ({ ...p, [id]: false })); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/ab-tests/${id}`, { headers });
      toast.success("A/B test deleted");
      fetchTests();
    } catch { toast.error("Failed to delete"); }
  };

  const fetchResults = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/ab-tests/${id}/results`, { headers });
      setResults(p => ({ ...p, [id]: res.data }));
    } catch { toast.error("Failed to load results"); }
  };

  const handlePickWinner = async (id, winner) => {
    try {
      await axios.post(`${API}/admin/ab-tests/${id}/pick-winner`, { winner }, { headers });
      toast.success(`Variant ${winner.toUpperCase()} declared winner!`);
      fetchResults(id);
      fetchTests();
    } catch { toast.error("Failed to pick winner"); }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      fetchResults(id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ab-test-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>
            A/B Subject Line Tests
          </h3>
          <p className={`text-xs mt-0.5 ${dm ? "text-gray-400" : "text-gray-500"}`}>
            Compare two subject lines to find what drives better opens and clicks
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setCreating(!creating)}
          className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl"
          data-testid="create-ab-test-btn"
        >
          {creating ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
          {creating ? "Cancel" : "New Test"}
        </Button>
      </div>

      {/* Create Form */}
      {creating && (
        <Card className={`rounded-2xl border-2 border-dashed ${dm ? "bg-gray-800/50 border-gray-600" : "border-gray-300"}`}>
          <CardContent className="p-5 space-y-4">
            <div>
              <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>Test Name</label>
              <Input
                placeholder="e.g., Holiday Campaign Subject Test"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                data-testid="ab-form-title"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-pink-500" /> Subject A
                  </span>
                </label>
                <Input
                  placeholder="First subject line variant"
                  value={form.subject_a}
                  onChange={e => setForm(f => ({ ...f, subject_a: e.target.value }))}
                  className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                  data-testid="ab-form-subject-a"
                />
              </div>
              <div>
                <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-cyan-500" /> Subject B
                  </span>
                </label>
                <Input
                  placeholder="Second subject line variant"
                  value={form.subject_b}
                  onChange={e => setForm(f => ({ ...f, subject_b: e.target.value }))}
                  className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                  data-testid="ab-form-subject-b"
                />
              </div>
            </div>
            <div>
              <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>Email Body (HTML)</label>
              <textarea
                placeholder="<p>Hello {{name}},</p><p>Check out our latest updates!</p>"
                value={form.body_html}
                onChange={e => setForm(f => ({ ...f, body_html: e.target.value }))}
                rows={3}
                className={`w-full rounded-xl border px-3 py-2 text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-100 placeholder:text-gray-500" : "border-gray-200"}`}
                data-testid="ab-form-body"
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={handleCreate} className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl" data-testid="ab-form-submit">
                <FlaskConical className="w-4 h-4 mr-1.5" /> Create A/B Test
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tests List */}
      {tests.length === 0 && !creating ? (
        <div className={`text-center py-16 rounded-2xl border-2 border-dashed ${dm ? "border-gray-700" : "border-gray-200"}`}>
          <FlaskConical className={`w-10 h-10 mx-auto mb-3 ${dm ? "text-gray-600" : "text-gray-300"}`} />
          <p className={`text-sm font-medium ${dm ? "text-gray-400" : "text-gray-500"}`}>No A/B tests yet</p>
          <p className={`text-xs mt-1 ${dm ? "text-gray-500" : "text-gray-400"}`}>Create your first test to compare subject lines</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tests.map(test => (
            <Card key={test.id} className={`rounded-2xl border overflow-hidden ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`} data-testid={`ab-test-${test.id}`}>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <h4 className={`text-sm font-bold truncate ${dm ? "text-gray-100" : "text-gray-900"}`}>
                        {test.message_title || "Untitled"}
                      </h4>
                      <Badge className={`text-[9px] px-1.5 shrink-0 ${STATUS_STYLES[test.status] || STATUS_STYLES.draft}`}>
                        {test.status}
                      </Badge>
                      {test.winner && (
                        <Badge className="text-[9px] px-1.5 bg-amber-500/10 text-amber-600 border-amber-500/20 shrink-0">
                          <Crown className="w-2.5 h-2.5 mr-0.5" /> Winner: {test.winner.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-4 text-xs">
                      <span className={dm ? "text-gray-400" : "text-gray-500"}>
                        <span className="inline-block w-2 h-2 rounded-full bg-pink-500 mr-1" />A: {test.subject_a?.substring(0, 45)}{test.subject_a?.length > 45 ? "..." : ""}
                      </span>
                      <span className={dm ? "text-gray-400" : "text-gray-500"}>
                        <span className="inline-block w-2 h-2 rounded-full bg-cyan-500 mr-1" />B: {test.subject_b?.substring(0, 45)}{test.subject_b?.length > 45 ? "..." : ""}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3 shrink-0">
                    {test.status === "draft" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSend(test.id)}
                        disabled={sending[test.id]}
                        className={`rounded-lg text-xs ${dm ? "border-gray-600 text-gray-200 hover:bg-gray-700" : ""}`}
                        data-testid={`ab-send-${test.id}`}
                      >
                        {sending[test.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                        Send
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleExpand(test.id)}
                      className={`rounded-lg text-xs ${dm ? "border-gray-600 text-gray-200 hover:bg-gray-700" : ""}`}
                      data-testid={`ab-expand-${test.id}`}
                    >
                      {expandedId === test.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </Button>
                    {test.status !== "sending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(test.id)}
                        className={`rounded-lg text-xs text-red-500 hover:text-red-600 ${dm ? "border-gray-600 hover:bg-gray-700" : ""}`}
                        data-testid={`ab-delete-${test.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Results */}
              {expandedId === test.id && (
                <ABResultsPanel
                  test={test}
                  results={results[test.id]}
                  dm={dm}
                  onPickWinner={(w) => handlePickWinner(test.id, w)}
                />
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


function ABResultsPanel({ test, results, dm, onPickWinner }) {
  if (!results) {
    return (
      <div className={`px-4 pb-4 flex justify-center`}>
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const { variant_a: a, variant_b: b } = results;
  const hasSent = a.sent > 0 || b.sent > 0;

  // Build horizontal bar data for the chart
  const barData = [
    { metric: "Open Rate", A: a.open_rate, B: b.open_rate },
    { metric: "Click Rate", A: a.click_rate, B: b.click_rate },
    { metric: "Sent", A: a.sent, B: b.sent },
    { metric: "Opened", A: a.opened, B: b.opened },
    { metric: "Clicked", A: a.clicked, B: b.clicked },
  ];

  return (
    <div className={`border-t px-4 pb-4 pt-3 space-y-4 ${dm ? "border-gray-700" : "border-gray-100"}`}>
      {/* Subject comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SubjectCard
          dm={dm}
          variant="A"
          subject={results.subject_a}
          color="pink"
          stats={a}
          isWinner={results.winner === "a"}
        />
        <SubjectCard
          dm={dm}
          variant="B"
          subject={results.subject_b}
          color="cyan"
          stats={b}
          isWinner={results.winner === "b"}
        />
      </div>

      {/* Horizontal Bar Chart */}
      {hasSent && (
        <Card className={`rounded-xl border ${dm ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
          <CardContent className="p-4">
            <h5 className={`text-xs font-semibold mb-3 ${dm ? "text-gray-300" : "text-gray-600"}`}>
              Performance Comparison
            </h5>
            <div className="h-[220px]" data-testid="ab-bar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={barData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
                  barGap={4}
                  barCategoryGap="25%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={dm ? "#1f2937" : "#e5e7eb"}
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    stroke={dm ? "#6b7280" : "#9ca3af"}
                    fontSize={10}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="metric"
                    stroke={dm ? "#9ca3af" : "#6b7280"}
                    fontSize={11}
                    width={70}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: dm ? "#111827" : "#fff",
                      border: `1px solid ${dm ? "#374151" : "#e5e7eb"}`,
                      borderRadius: "10px",
                      color: dm ? "#f3f4f6" : "#111827",
                      fontSize: 12,
                    }}
                    cursor={{ fill: dm ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)" }}
                  />
                  <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                  <Bar
                    dataKey="A"
                    name="Variant A"
                    fill="#ec4899"
                    radius={[0, 6, 6, 0]}
                    barSize={14}
                  />
                  <Bar
                    dataKey="B"
                    name="Variant B"
                    fill="#06b6d4"
                    radius={[0, 6, 6, 0]}
                    barSize={14}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pick Winner */}
      {test.status === "completed" && !results.winner && hasSent && (
        <div className={`flex items-center justify-center gap-3 p-3 rounded-xl border-2 border-dashed ${dm ? "border-gray-600" : "border-gray-300"}`}>
          <Trophy className={`w-4 h-4 ${dm ? "text-amber-400" : "text-amber-500"}`} />
          <span className={`text-xs font-medium ${dm ? "text-gray-300" : "text-gray-600"}`}>Declare a winner:</span>
          <Button
            size="sm"
            onClick={() => onPickWinner("a")}
            className="bg-pink-500 hover:bg-pink-600 text-white rounded-lg text-xs px-3"
            data-testid="pick-winner-a"
          >
            <Crown className="w-3 h-3 mr-1" /> Variant A
          </Button>
          <Button
            size="sm"
            onClick={() => onPickWinner("b")}
            className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-xs px-3"
            data-testid="pick-winner-b"
          >
            <Crown className="w-3 h-3 mr-1" /> Variant B
          </Button>
        </div>
      )}

      {!hasSent && (
        <p className={`text-xs text-center py-3 ${dm ? "text-gray-500" : "text-gray-400"}`}>
          Send the test to see results
        </p>
      )}
    </div>
  );
}


function SubjectCard({ dm, variant, subject, color, stats, isWinner }) {
  const dotColor = color === "pink" ? "bg-pink-500" : "bg-cyan-500";
  const borderColor = isWinner
    ? "border-amber-400 ring-2 ring-amber-400/20"
    : dm ? "border-gray-700" : "border-gray-200";

  return (
    <div className={`p-3 rounded-xl border ${borderColor} ${dm ? "bg-gray-800/50" : "bg-white"}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-3 h-3 rounded-full ${dotColor} shrink-0`} />
        <span className={`text-xs font-bold ${dm ? "text-gray-200" : "text-gray-700"}`}>
          Variant {variant}
        </span>
        {isWinner && (
          <Badge className="text-[9px] px-1 bg-amber-500/10 text-amber-500 border-amber-500/20 ml-auto">
            <Crown className="w-2.5 h-2.5 mr-0.5" /> Winner
          </Badge>
        )}
      </div>
      <p className={`text-xs mb-3 leading-relaxed ${dm ? "text-gray-300" : "text-gray-600"}`}>
        &ldquo;{subject}&rdquo;
      </p>
      <div className="grid grid-cols-3 gap-2">
        <MiniStat dm={dm} icon={Send} label="Sent" value={stats.sent} />
        <MiniStat dm={dm} icon={Eye} label="Opens" value={stats.opened} extra={`${stats.open_rate}%`} />
        <MiniStat dm={dm} icon={MousePointerClick} label="Clicks" value={stats.clicked} extra={`${stats.click_rate}%`} />
      </div>
    </div>
  );
}


function MiniStat({ dm, icon: Icon, label, value, extra }) {
  return (
    <div className={`text-center p-1.5 rounded-lg ${dm ? "bg-gray-900/50" : "bg-gray-50"}`}>
      <Icon className={`w-3 h-3 mx-auto mb-0.5 ${dm ? "text-gray-500" : "text-gray-400"}`} />
      <p className={`text-sm font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>{value}</p>
      <p className={`text-[9px] ${dm ? "text-gray-500" : "text-gray-400"}`}>{label}</p>
      {extra && <p className={`text-[9px] font-semibold ${dm ? "text-gray-400" : "text-gray-500"}`}>{extra}</p>}
    </div>
  );
}
